import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'buzzzillion_wishlist_guest';

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState([]);

  // Load wishlist: from server if logged in, from localStorage if guest
  const loadWishlist = useCallback(async () => {
    if (user) {
      try {
        const r = await axios.get(`${API}/wishlist`, { withCredentials: true });
        setWishlistIds(r.data.product_ids || []);
      } catch { setWishlistIds([]); }
    } else {
      const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      setWishlistIds(stored);
    }
  }, [user]);

  // Sync guest wishlist to server on login
  useEffect(() => {
    if (user) {
      const guestIds = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      if (guestIds.length > 0) {
        axios.post(`${API}/wishlist/sync`, { product_ids: guestIds }, { withCredentials: true })
          .then(r => { setWishlistIds(r.data.product_ids || []); localStorage.removeItem(LS_KEY); })
          .catch(() => loadWishlist());
      } else {
        loadWishlist();
      }
    } else {
      loadWishlist();
    }
  }, [user]);

  const toggleWishlist = async (productId) => {
    const inList = wishlistIds.includes(productId);
    if (user) {
      if (inList) {
        await axios.delete(`${API}/wishlist/remove/${productId}`, { withCredentials: true });
        setWishlistIds(prev => prev.filter(id => id !== productId));
      } else {
        await axios.post(`${API}/wishlist/add`, { product_id: productId }, { withCredentials: true });
        setWishlistIds(prev => [...prev, productId]);
      }
    } else {
      const next = inList ? wishlistIds.filter(id => id !== productId) : [...wishlistIds, productId];
      setWishlistIds(next);
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    }
  };

  const isWishlisted = (productId) => wishlistIds.includes(productId);

  return (
    <WishlistContext.Provider value={{ wishlistIds, toggleWishlist, isWishlisted, count: wishlistIds.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
